/* GuidanceBanner · Founder Operating System · Batch P2.3 · Feb 2026
 * ====================================================================
 * Lightweight, contextual, non-intrusive banner that renders the SINGLE
 * next nudge from the Continuous Workflow Engine. Mounts inside
 * existing OS surfaces (Today, Prep Card, Work Mode) — never a page
 * of its own. Deterministic upstream: same state → same message.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Compass } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Tone table mirrors Today.js STATE_LABEL — kept minimal so the banner
// blends into whichever surface it mounts on.
const TONE = {
  today:            "bg-slate-50   border-slate-200   text-slate-700",
  preparing:        "bg-amber-50   border-amber-200   text-amber-800",
  travelling:       "bg-orange-50  border-orange-200  text-orange-800",
  work_mode:        "bg-emerald-50 border-emerald-200 text-emerald-800",
  reflection:       "bg-sky-50     border-sky-200     text-sky-800",
  between_lessons:  "bg-slate-50   border-slate-200   text-slate-700",
  business:         "bg-slate-50   border-slate-200   text-slate-700",
};

export function GuidanceBanner({ hideWhenQuiet = false }) {
  const [g, setG] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/workday/guidance`, {
          withCredentials: true,
        });
        if (!cancelled) {
          setG(r.data);
          setState("ok");
        }
      } catch {
        if (!cancelled) setState("error");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (state !== "ok" || !g || !g.message) return null;
  // The "today" state is the ambient default; some surfaces prefer to
  // stay silent then rather than draw attention. Truthful either way —
  // the message itself is unchanged.
  if (hideWhenQuiet && g.state === "today") return null;

  const tone = TONE[g.state] || TONE.today;

  return (
    <div
      data-testid="guidance-banner"
      data-guidance-state={g.state}
      className={`rounded-xl border ${tone} px-3 py-2 text-sm flex items-center justify-between gap-3`}
    >
      <span data-testid="guidance-banner-message" className="font-medium">
        {g.message}
      </span>
      {g.action_target && (
        <Link
          to={g.action_target}
          data-testid="guidance-banner-action"
          className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold underline underline-offset-2"
        >
          <Compass className="w-3.5 h-3.5" />
          Open
        </Link>
      )}
    </div>
  );
}
